export enum MediaFileTypes {
  PDF = 'application/pdf',
  CSV = 'text/csv',
  TEXT = 'text/plain',
  WORD = 'application/msword',
  DOCX = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  EXCEL = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',

  JPEG = 'image/jpeg',
  PNG = 'image/png',
  GIF = 'image/gif',
  WEBP = 'image/webp',
  BMP = 'image/bmp',
  TIFF = 'image/tiff',
  HEIC = 'image/heic',
  HEIF = 'image/heif',

  MP4 = 'video/mp4',
  WEBM = 'video/webm',
  OGV = 'video/ogg',
  AVI = 'video/x-msvideo',
  MKV = 'video/x-matroska',
  MOV = 'video/quicktime',
  FLV = 'video/x-flv',
  MPEG = 'video/mpeg'
}

const ALLOWED_MIME_TYPES: ReadonlySet<string> = new Set(
  Object.values(MediaFileTypes)
);

const EXTENSION_TO_MIME: Readonly<Record<string, MediaFileTypes>> = {
  pdf: MediaFileTypes.PDF,
  csv: MediaFileTypes.CSV,
  txt: MediaFileTypes.TEXT,
  doc: MediaFileTypes.WORD,
  docx: MediaFileTypes.DOCX,
  xlsx: MediaFileTypes.EXCEL,

  jpg: MediaFileTypes.JPEG,
  jpeg: MediaFileTypes.JPEG,
  png: MediaFileTypes.PNG,
  gif: MediaFileTypes.GIF,
  webp: MediaFileTypes.WEBP,
  bmp: MediaFileTypes.BMP,
  tif: MediaFileTypes.TIFF,
  tiff: MediaFileTypes.TIFF,
  heic: MediaFileTypes.HEIC,
  heif: MediaFileTypes.HEIF,

  mp4: MediaFileTypes.MP4,
  webm: MediaFileTypes.WEBM,
  ogv: MediaFileTypes.OGV,
  avi: MediaFileTypes.AVI,
  mkv: MediaFileTypes.MKV,
  mov: MediaFileTypes.MOV,
  flv: MediaFileTypes.FLV,
  mpeg: MediaFileTypes.MPEG,
  mpg: MediaFileTypes.MPEG
};

export const ALLOWED_EXTENSIONS: ReadonlyArray<string> =
  Object.keys(EXTENSION_TO_MIME);

export const ACCEPT_ATTRIBUTE: string = [
  ...ALLOWED_EXTENSIONS.map((ext) => `.${ext}`),
  ...Array.from(ALLOWED_MIME_TYPES)
].join(',');

const getExtension = (fileName: string): string => {
  const lastDot = fileName.lastIndexOf('.');
  if (lastDot < 0 || lastDot === fileName.length - 1) {
    return '';
  }
  return fileName.slice(lastDot + 1).toLowerCase();
};

export type UploadValidationResult =
  | { ok: true; contentType: MediaFileTypes }
  | { ok: false; reason: string };

export const validateUpload = (file: File): UploadValidationResult => {
  const extension = getExtension(file.name);
  if (!extension) {
    return { ok: false, reason: `"${file.name}" has no file extension.` };
  }

  const extensionMime = EXTENSION_TO_MIME[extension];
  if (!extensionMime) {
    return {
      ok: false,
      reason: `"${file.name}" has an unsupported file type (.${extension}).`
    };
  }

  // file.type can be empty or browser-inferred — when present, require it to
  // match both the allowlist AND the extension's expected MIME, so an attacker
  // can't smuggle e.g. video/mp4 bytes under a .jpg extension.
  if (file.type) {
    if (!ALLOWED_MIME_TYPES.has(file.type)) {
      return {
        ok: false,
        reason: `"${file.name}" has an unsupported content type (${file.type}).`
      };
    }
    if (file.type !== extensionMime) {
      return {
        ok: false,
        reason: `"${file.name}" content type (${file.type}) does not match its .${extension} extension.`
      };
    }
  }

  return { ok: true, contentType: extensionMime };
};

export const sanitizeFileName = (name: string): string => {
  const extension = getExtension(name);
  const base = extension
    ? name.slice(0, name.length - extension.length - 1)
    : name;

  const cleanedBase = base
    .replace(/[/\\]/g, '_')
    // eslint-disable-next-line no-control-regex
    .replace(/[\x00-\x1f\x7f]/g, '')
    .replace(/[^A-Za-z0-9._-]/g, '_')
    .replace(/_+/g, '_')
    .replace(/\.+/g, '.')
    .replace(/^[._-]+|[._-]+$/g, '');

  const safeBase = cleanedBase.length > 0 ? cleanedBase : 'file';
  return extension ? `${safeBase}.${extension}` : safeBase;
};
